<script setup>
import Header from "@/components/Header.vue";
import { ref, onMounted, computed } from "vue";
import heroImage from "@/assets/img/background/image3.png";
import { loadSlim } from "tsparticles-slim";
import {
  getTorneoActivo,
  getPrecioInscripcion,
  getCategoriasPeso,
} from "@/utils/api_backend/";

const precios = ref([]);
const categorias = ref([]);
const cargando = ref(true);
const tabFiltro = ref("gi"); // 'gi' | 'nogi'

const categoriasVaronil = computed(() =>
  categorias.value.filter((c) => c.genero === "H" && c.modalidad === tabFiltro.value)
);
const categoriasFemenil = computed(() =>
  categorias.value.filter((c) => c.genero === "M" && c.modalidad === tabFiltro.value)
);

onMounted(async () => {
  try {
    const torneo = await getTorneoActivo();
    console.log("torneo:", torneo.id);
    if (!torneo) return;

    const [resPrecios, resCategorias] = await Promise.all([
      getPrecioInscripcion(torneo.id),
      getCategoriasPeso(torneo.id),
    ]);

    precios.value = resPrecios.sort((a, b) => {
      if (a.modalidad_combate !== b.modalidad_combate)
        return a.modalidad_combate.localeCompare(b.modalidad_combate);
      return a.grupo_edad.localeCompare(b.grupo_edad);
    });

    categorias.value = resCategorias;
  } catch (error) {
    console.error("Error cargando datos del torneo:", error);
  } finally {
    cargando.value = false;
  }
});

const formatPrecio = (valor) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(valor);

const formatFecha = (fechaString) => {
  if (!fechaString) return "";
  const [year, month, day] = fechaString.split("-");
  return new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const labelModalidad = (m) => ({ gi: "GI", nogi: "NO-GI" }[m] ?? m.toUpperCase());
const labelGrupoEdad = (g) => ({ adulto: "Adulto", juvenil: "Juvenil" }[g] ?? g);
const labelDivision = (d) =>
  ({
    rooster: "Rooster",
    light_feather: "Light Feather",
    feather: "Feather",
    light: "Light",
    middle: "Middle",
    medium_heavy: "Medium Heavy",
    heavy: "Heavy",
    super_heavy: "Super Heavy",
    ultra_heavy: "Ultra Heavy",
  }[d] ?? d);

const accentModalidad = (m) => (m === "gi" ? "#00aaff" : "#ff6b00");

const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#00aaff" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};
const particlesInit = async (engine) => await loadSlim(engine);
</script>

<template>
  <section id="torneo" class="torneo-wrapper">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="torneoParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <div class="contenido">
      <Header />

      <!-- ══ PRECIOS ══ -->
      <div class="seccion" data-aos="fade-up">
        <div class="seccion-header">
          <span class="seccion-linea"></span>
          <h2 class="seccion-titulo">Costos de <em>Inscripción</em></h2>
          <span class="seccion-linea"></span>
        </div>
        <p class="seccion-sub">Tarifas vigentes a la fecha</p>

        <div v-if="cargando" class="estado-centro">
          <div class="spinner">
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>

        <div v-else-if="precios.length" class="precios-grid">
          <div
            v-for="(item, i) in precios"
            :key="i"
            class="card-precio"
            :style="{ '--accent': accentModalidad(item.modalidad_combate) }"
          >
            <div class="card-precio__glow"></div>
            <div class="card-precio__header">
              <span class="badge-modalidad">{{
                labelModalidad(item.modalidad_combate)
              }}</span>
              <span class="badge-edad">{{ labelGrupoEdad(item.grupo_edad) }}</span>
            </div>
            <p class="card-precio__monto">{{ formatPrecio(item.precio) }}</p>
            <p class="card-precio__vigencia">
              Válido hasta {{ formatFecha(item.fecha_fin) }}
            </p>
            <div class="card-precio__line"></div>
          </div>
        </div>

        <p v-else class="vacio-msg">Precios no disponibles aún.</p>
      </div>

      <!-- ══ CATEGORÍAS ══ -->
      <div class="seccion" data-aos="fade-up" data-aos-delay="100">
        <div class="seccion-header">
          <span class="seccion-linea"></span>
          <h2 class="seccion-titulo">Categorías <em>Oficiales</em></h2>
          <span class="seccion-linea"></span>
        </div>
        <p class="seccion-sub">Divisiones IBJJF por modalidad</p>

        <div class="tabs">
          <button
            class="tab-btn"
            :class="{ active: tabFiltro === 'gi' }"
            @click="tabFiltro = 'gi'"
          >
            GI
          </button>
          <button
            class="tab-btn nogi"
            :class="{ active: tabFiltro === 'nogi' }"
            @click="tabFiltro = 'nogi'"
          >
            NO-GI
          </button>
        </div>

        <div v-if="cargando" class="estado-centro">
          <div class="spinner">
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>

        <div v-else class="tablas-grid">
          <div class="tabla-bloque" data-aos="fade-right">
            <div class="tabla-header tag-h">Categoría Varonil</div>
            <table class="tabla-cat">
              <thead>
                <tr>
                  <th>División</th>
                  <th>Edad</th>
                  <th>Peso máx.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cat in categoriasVaronil" :key="cat.id">
                  <td class="td-division">{{ labelDivision(cat.division) }}</td>
                  <td class="td-edad">{{ labelGrupoEdad(cat.grupo_edad) }}</td>
                  <td class="td-peso">
                    {{ cat.peso_max_kg ? cat.peso_max_kg + " kg" : "Open" }}
                  </td>
                </tr>
                <tr v-if="!categoriasVaronil.length">
                  <td colspan="3" class="td-vacio">Sin categorías disponibles</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="tabla-bloque" data-aos="fade-left">
            <div class="tabla-header tag-m">Categoría Femenil</div>
            <table class="tabla-cat">
              <thead>
                <tr>
                  <th>División</th>
                  <th>Edad</th>
                  <th>Peso máx.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cat in categoriasFemenil" :key="cat.id">
                  <td class="td-division">{{ labelDivision(cat.division) }}</td>
                  <td class="td-edad">{{ labelGrupoEdad(cat.grupo_edad) }}</td>
                  <td class="td-peso">
                    {{ cat.peso_max_kg ? cat.peso_max_kg + " kg" : "Open" }}
                  </td>
                </tr>
                <tr v-if="!categoriasFemenil.length">
                  <td colspan="3" class="td-vacio">Sin categorías disponibles</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.torneo-wrapper {
  position: relative;
  min-height: 100vh;
  padding-bottom: 5rem;
  overflow: hidden;
}
.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.3) contrast(1.1);
  z-index: 1;
}
.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, #000 0%, rgba(0, 20, 45, 0.8) 50%, #000 100%);
  z-index: 2;
}
#torneoParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}
.smoke-layer {
  position: absolute;
  inset: 0;
  background: url("https://c4.wallpaperflare.com/wallpaper/448/699/737/abstract-digital-art-3d-abstract-lines-wallpaper-preview.jpg");
  opacity: 0.15;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}
@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.1);
  }
  100% {
    transform: translateY(-10%) scale(1.15);
  }
}
.contenido {
  position: relative;
  z-index: 10;
}
.seccion {
  width: min(1100px, 92vw);
  margin: 0 auto;
  padding-top: 4rem;
}
.seccion-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 0.4rem;
}
.seccion-linea {
  flex: 1;
  max-width: 100px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 170, 255, 0.4));
}
.seccion-header .seccion-linea:last-child {
  background: linear-gradient(90deg, rgba(0, 170, 255, 0.4), transparent);
}
.seccion-titulo {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 2px;
  white-space: nowrap;
  margin: 0;
}
.seccion-titulo em {
  font-style: normal;
  color: transparent;
  -webkit-text-stroke: 1.5px #00aaff;
}
.seccion-sub {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(0, 170, 255, 0.7);
  margin: 0 0 2rem;
}
.estado-centro {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}
.spinner {
  position: relative;
  width: 40px;
  height: 40px;
}
.spinner div {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #00aaff;
  animation: giro 1s linear infinite;
}
.spinner div:nth-child(2) {
  inset: 7px;
  border-top-color: rgba(0, 170, 255, 0.5);
  animation-duration: 1.6s;
  animation-direction: reverse;
}
.spinner div:nth-child(3) {
  inset: 14px;
  border-top-color: rgba(0, 170, 255, 0.2);
  animation-duration: 2.2s;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}
.precios-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}
.card-precio {
  position: relative;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--accent, #00aaff);
  border-radius: 20px;
  padding: 36px 32px 28px;
  text-align: center;
  backdrop-filter: blur(12px);
  width: 220px;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.card-precio:hover {
  transform: translateY(-6px);
  box-shadow: 0 0 28px rgba(0, 170, 255, 0.2);
}
.card-precio__glow {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, var(--accent, #00aaff) 0%, transparent 70%);
  opacity: 0.12;
  pointer-events: none;
}
.card-precio__header {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
}
.badge-modalidad {
  background: var(--accent, #00aaff);
  color: #000;
  font-size: 0.6rem;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 20px;
}
.badge-edad {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.card-precio__monto {
  font-family: "Oswald", sans-serif;
  font-size: 2.4rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px;
}
.card-precio__vigencia {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.832);
  margin: 0;
  letter-spacing: 0.5px;
}
.card-precio__line {
  height: 1px;
  margin-top: 20px;
  background: linear-gradient(90deg, transparent, var(--accent, #00aaff), transparent);
  opacity: 0.35;
}
.vacio-msg {
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.9rem;
  padding: 2rem 0;
}
.tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 2rem;
}
.tab-btn {
  padding: 8px 28px;
  border-radius: 4px;
  border: 1px solid rgba(0, 170, 255, 0.4);
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-family: "Oswald", sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tab-btn.active {
  background: #00aaff;
  border-color: #00aaff;
  color: #000;
}
.tab-btn.nogi {
  border-color: rgba(255, 107, 0, 0.4);
}
.tab-btn.nogi.active {
  background: #ff6b00;
  border-color: #ff6b00;
  color: #000;
}
.tablas-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.tabla-bloque {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.03);
}
.tabla-header {
  padding: 12px 20px;
  font-family: "Oswald", sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
}
.tag-h {
  background: rgba(0, 85, 255, 0.2);
  color: #5599ff;
  border-bottom: 1px solid rgba(0, 85, 255, 0.25);
}
.tag-m {
  background: rgba(255, 0, 119, 0.15);
  color: #ff5599;
  border-bottom: 1px solid rgba(255, 0, 119, 0.2);
}
.tabla-cat {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.tabla-cat thead tr {
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.tabla-cat th {
  padding: 10px 16px;
  text-align: left;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
}
.tabla-cat tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.15s ease;
}
.tabla-cat tbody tr:hover {
  background: rgba(0, 170, 255, 0.05);
}
.tabla-cat tbody tr:last-child {
  border-bottom: none;
}
.td-division {
  padding: 10px 16px;
  color: #fff;
  font-weight: 600;
}
.td-edad {
  padding: 10px 16px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.78rem;
}
.td-peso {
  padding: 10px 16px;
  color: #00aaff;
  font-weight: 700;
  font-size: 0.9rem;
}
.td-vacio {
  padding: 20px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.25);
  font-size: 0.8rem;
}
@media (max-width: 768px) {
  .tablas-grid {
    grid-template-columns: 1fr;
  }
  .precios-grid {
    gap: 14px;
  }
  .card-precio {
    width: 160px;
    padding: 28px 20px 22px;
  }
  .card-precio__monto {
    font-size: 1.9rem;
  }
}
@media (max-width: 480px) {
  .card-precio {
    width: 140px;
  }
  .seccion-titulo {
    font-size: 1.2rem;
  }
}
</style>
